import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'antd';
import { v7 as uuidv7 } from 'uuid';
import { createGlobalStyle } from 'styled-components';
import { Button, Tooltip } from 'components/common';
import Summary from './Summary';
import { downloadType } from './Summary/constants';
import { useSelector } from 'react-redux';
import { exportTemplateSelectors } from 'reducers/exportTemplate/reducer';
import ExportTemplatesModal from '../ExportTemplates/ExportTemplatesModal';
const GlobalStyle = createGlobalStyle`
  .export-summary:hover {
    background-color: initial;
    cursor: initial;
  }
`;

function Export({
  onExport,
  disabled,
  downloadType,
  getQueryBody,
  children,
  ewrca,
  size = 'small',
  style = {},
  canUseCredits = true,
  ewrcaTooltip,
  icon,
  showExportTemplateSection = false,
  buttonTestId = 'export-button',
}) {
  const [open, setOpen] = useState(false);
  const [transactionId, setTransactionId] = useState(uuidv7());

  const showExportTemplatesModal = useSelector(
    exportTemplateSelectors.isModalOpen
  );

  useEffect(() => {
    if (!disabled) {
      setTransactionId(uuidv7());
    }
  }, [disabled]);

  const regenerateTransactionId = useCallback(() => {
    setTransactionId(uuidv7());
  }, []);

  const items = useMemo(
    () =>
      !open
        ? []
        : [
            {
              key: 1,
              label: (
                <Summary
                  transactionId={transactionId}
                  regenerateTransactionId={regenerateTransactionId}
                  downloadType={downloadType}
                  onClose={() => setOpen(false)}
                  getQueryBody={getQueryBody}
                  onExport={onExport}
                  canUseCredits={canUseCredits}
                  showExportTemplateSection={showExportTemplateSection}
                />
              ),
              className: 'export-summary',
              style: {
                padding: '10px',
                color: '#000000',
                backgroundColor: 'white',
              },
            },
          ],
    [
      downloadType,
      transactionId,
      getQueryBody,
      onExport,
      regenerateTransactionId,
      open,
      canUseCredits,
      showExportTemplateSection,
    ]
  );

  const handleOpenChange = nextOpen => {
    if (showExportTemplatesModal) {
      return;
    }
    setOpen(nextOpen);

    if (nextOpen) {
      regenerateTransactionId();
    }
  };

  return (
    <Tooltip title={ewrca ? ewrcaTooltip : ''}>
      <ExportTemplatesModal />
      <GlobalStyle />
      <Dropdown
        disabled={disabled}
        menu={{
          items,
          onClick: () => null,
          onKeyDown: e => {
            if (showExportTemplatesModal) return;
            if (e.key !== 'Escape') {
              e.stopPropagation();
            }
          },
        }}
        onOpenChange={handleOpenChange}
        open={open}
        destroyPopupOnHide
        trigger={['click']}
        getPopupContainer={triggerNode => triggerNode.parentElement}
      >
        <Button
          data-testid={buttonTestId}
          style={style}
          size={size}
          icon={icon}
        >
          {children ? children : 'Export'}
        </Button>
      </Dropdown>
    </Tooltip>
  );
}

Export.propTypes = {
  onExport: PropTypes.func,
  downloadType: PropTypes.oneOf(
    Object.keys(downloadType).map(k => downloadType[k])
  ),
  size: PropTypes.oneOf(['small', 'middle', 'large', '1x']),
  label: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  getQueryBody: PropTypes.func,
  ewrca: PropTypes.bool,
  ewrcaTooltip: PropTypes.string,
  icon: PropTypes.element,
  style: PropTypes.shape({}),
  showExportTemplateSection: PropTypes.bool,
  buttonTestId: PropTypes.string,
};

export default Export;
